/** QuickRouter — Bibliothèque d'empreintes intégrées.
 *
 * Coordonnées en mm, Y vers le HAUT, origine = centre de l'empreinte (sauf connecteurs :
 * broche 1). Les cotes des CMS reprennent les empreintes KiCad (IPC-7351 nominal) ;
 * celles des modules (ESP32…) la fiche du fabricant. Une empreinte de « pièce » (régulateur,
 * émetteur RS485…) porte les NOMS de broches et les références d'achat (defaults).
 *
 * Les barrettes au pas de 2,54 sont générées à la demande : `pinheader-1x08`,
 * `pinheader-2x20`, `pinsocket-1x06`…  (voir dynamicFootprint).
 */
import { FootprintSchema } from '../schema.js';

const r3 = (v) => Math.round(v * 1000) / 1000;

function fp(def) {
  return FootprintSchema.parse(def);
}

/** Contour de sérigraphie rectangulaire centré, avec repère de broche 1 optionnel. */
function silkBox(w, h, cx = 0, cy = 0) {
  const x0 = r3(cx - w / 2), x1 = r3(cx + w / 2), y0 = r3(cy - h / 2), y1 = r3(cy + h / 2);
  return [{ kind: 'poly', pts: [[x0, y0], [x1, y0], [x1, y1], [x0, y1], [x0, y0]], width: 0.15 }];
}

const pin1Dot = (x, y) => ({ kind: 'circle', pts: [[r3(x), r3(y)]], r: 0.25, width: 0.15, fill: true });

// ── Composants passifs CMS (2 broches) ─────────────────────────

const CHIP = {
  '0402': { pad: [0.54, 0.64], at: 0.48, body: [1.0, 0.5, 0.35] },
  '0603': { pad: [0.8, 0.95], at: 0.825, body: [1.6, 0.8, 0.45] },
  '0805': { pad: [1.025, 1.4], at: 0.9125, body: [2.0, 1.25, 0.5] },
  '1206': { pad: [1.125, 1.75], at: 1.4625, body: [3.2, 1.6, 0.55] },
};

function chip(kind, size, extra = {}) {
  const c = CHIP[size];
  const names = extra.names ?? ['', ''];
  const label = { R: 'Résistance', C: 'Condensateur', L: 'Inductance', D: 'LED', F: 'Fusible' }[kind];
  return fp({
    id: `${kind.toLowerCase()}-${size}`,
    name: `${label} ${size}`,
    category: 'Passifs',
    refPrefix: kind,
    description: `${label} CMS boîtier ${size} (impérial). Broche 1 à gauche (X−), broche 2 à droite.${kind === 'D' ? ' LED : 1 = cathode (K), 2 = anode (A).' : ''}`,
    pads: [
      { num: '1', name: names[0], shape: 'roundrect', pos: [-c.at, 0], size: c.pad },
      { num: '2', name: names[1], shape: 'roundrect', pos: [c.at, 0], size: c.pad },
    ],
    silk: kind === 'D' ? [{ kind: 'line', pts: [[-c.at - c.pad[0] / 2 - 0.25, -c.pad[1] / 2], [-c.at - c.pad[0] / 2 - 0.25, c.pad[1] / 2]], width: 0.15 }] : [],
    courtyard: [[r3(-c.at - c.pad[0] / 2 - 0.25), r3(-c.pad[1] / 2 - 0.25)], [r3(c.at + c.pad[0] / 2 + 0.25), r3(c.pad[1] / 2 + 0.25)]],
    body: { size: c.body, color: { R: '#1b1b1b', C: '#b89a6a', L: '#333', D: '#e8e8e0', F: '#ddd' }[kind] },
    tags: [kind, size],
    defaults: extra.defaults,
  });
}

// ── Boîtiers SO / SOT ──────────────────────────────────────────

/** SOIC/SSOP à deux rangées : n broches, pas p, entraxe des rangées span, pastille pad. */
function soic(id, name, n, { pitch = 1.27, span = 4.95, pad = [1.95, 0.6], body = [3.9, 4.9, 1.5], names = [], category = 'Circuits intégrés', description = '', defaults, refPrefix = 'U' } = {}) {
  const half = n / 2;
  const pads = [];
  const y0 = ((half - 1) * pitch) / 2;
  for (let k = 0; k < half; k++) {
    pads.push({ num: String(k + 1), name: names[k] ?? '', shape: 'roundrect', pos: [r3(-span / 2), r3(y0 - k * pitch)], size: pad });
  }
  for (let k = 0; k < half; k++) {
    pads.push({ num: String(half + k + 1), name: names[half + k] ?? '', shape: 'roundrect', pos: [r3(span / 2), r3(-y0 + k * pitch)], size: pad });
  }
  const cw = span + pad[0] + 0.5;
  const ch = Math.max(body[1], (half - 1) * pitch + pad[1]) + 0.5;
  return fp({
    id, name, category, refPrefix,
    description: description || `${n} broches, pas ${pitch} mm. Broche 1 en haut à gauche, numérotation dans le sens trigonométrique.`,
    pads,
    silk: [...silkBox(body[0] - 0.4, body[1]), pin1Dot(-span / 2 - pad[0] / 2 - 0.2, y0 + pad[1])],
    courtyard: [[r3(-cw / 2), r3(-ch / 2)], [r3(cw / 2), r3(ch / 2)]],
    body: { size: body, color: '#1c1c1c' },
    tags: ['SOIC', `${n}`],
    defaults,
  });
}

function sot23(id, name, names = ['', '', ''], extra = {}) {
  return fp({
    id, name, category: extra.category ?? 'Discrets', refPrefix: extra.refPrefix ?? 'Q',
    description: extra.description ?? 'SOT-23 : broches 1 (haut gauche) et 2 (bas gauche), broche 3 à droite.',
    pads: [
      { num: '1', name: names[0], shape: 'roundrect', pos: [-1.1375, 0.95], size: [1.475, 0.6] },
      { num: '2', name: names[1], shape: 'roundrect', pos: [-1.1375, -0.95], size: [1.475, 0.6] },
      { num: '3', name: names[2], shape: 'roundrect', pos: [1.1375, 0], size: [1.475, 0.6] },
    ],
    silk: [pin1Dot(-1.6, 1.6)],
    courtyard: [[-2.1, -1.7], [2.1, 1.7]],
    body: { size: [1.3, 2.9, 1.1], color: '#1c1c1c' },
    tags: ['SOT-23'],
    defaults: extra.defaults,
  });
}

function sot23_5(id, name, names = ['', '', '', '', ''], extra = {}) {
  return fp({
    id, name, category: extra.category ?? 'Circuits intégrés', refPrefix: extra.refPrefix ?? 'U',
    description: extra.description ?? 'SOT-23-5 : 1-2-3 à gauche de haut en bas, 4-5 à droite de bas en haut.',
    pads: [
      { num: '1', name: names[0], shape: 'roundrect', pos: [-1.1375, 0.95], size: [1.325, 0.6] },
      { num: '2', name: names[1], shape: 'roundrect', pos: [-1.1375, 0], size: [1.325, 0.6] },
      { num: '3', name: names[2], shape: 'roundrect', pos: [-1.1375, -0.95], size: [1.325, 0.6] },
      { num: '4', name: names[3], shape: 'roundrect', pos: [1.1375, -0.95], size: [1.325, 0.6] },
      { num: '5', name: names[4], shape: 'roundrect', pos: [1.1375, 0.95], size: [1.325, 0.6] },
    ],
    silk: [pin1Dot(-1.6, 1.6)],
    courtyard: [[-2.1, -1.7], [2.1, 1.7]],
    body: { size: [1.6, 2.9, 1.1], color: '#1c1c1c' },
    tags: ['SOT-23-5'],
    defaults: extra.defaults,
  });
}

function sot223(id, name, names = ['', '', '', ''], extra = {}) {
  return fp({
    id, name, category: extra.category ?? 'Alimentation', refPrefix: extra.refPrefix ?? 'U',
    description: extra.description ?? 'SOT-223 : broches 1-2-3 en bas (gauche → droite), languette (4) en haut, reliée à la broche 2.',
    pads: [
      { num: '1', name: names[0], shape: 'roundrect', pos: [-2.3, -3.15], size: [1.5, 2.0] },
      { num: '2', name: names[1], shape: 'roundrect', pos: [0, -3.15], size: [1.5, 2.0] },
      { num: '3', name: names[2], shape: 'roundrect', pos: [2.3, -3.15], size: [1.5, 2.0] },
      { num: '4', name: names[3] || names[1], shape: 'roundrect', pos: [0, 3.15], size: [3.8, 2.0] },
    ],
    silk: [...silkBox(6.5, 3.5)],
    courtyard: [[-3.6, -4.4], [3.6, 4.4]],
    body: { size: [6.5, 3.5, 1.7], color: '#1c1c1c' },
    tags: ['SOT-223'],
    defaults: extra.defaults,
  });
}

function twoPinDiode(id, name, { pad, at, body, defaults, description }) {
  return fp({
    id, name, category: 'Discrets', refPrefix: 'D',
    description: description ?? '1 = cathode (K, côté du trait), 2 = anode (A).',
    pads: [
      { num: '1', name: 'K', shape: 'roundrect', pos: [-at, 0], size: pad },
      { num: '2', name: 'A', shape: 'roundrect', pos: [at, 0], size: pad },
    ],
    silk: [{ kind: 'line', pts: [[-at - pad[0] / 2 - 0.3, -pad[1] / 2], [-at - pad[0] / 2 - 0.3, pad[1] / 2]], width: 0.2 }],
    courtyard: [[r3(-at - pad[0] / 2 - 0.4), r3(-Math.max(pad[1], body[1]) / 2 - 0.3)], [r3(at + pad[0] / 2 + 0.4), r3(Math.max(pad[1], body[1]) / 2 + 0.3)]],
    body: { size: body, color: '#1c1c1c' },
    tags: ['diode'],
    defaults,
  });
}

// ── Traversants ────────────────────────────────────────────────

/** Barrette au pas 2,54 : broche 1 à l'origine, colonnes vers +X, 2e rangée vers −Y.
 *  Numérotation en zigzag (1 = rangée 1, 2 = rangée 2 en face) comme KiCad. */
export function pinHeader(id, name, cols, rows = 1, { names = [], female = false, category = 'Connecteurs', description = '', pitch = 2.54, drill = 1.0, padSize = 1.7, rowDir = -1 } = {}) {
  const pads = [];
  let k = 0;
  for (let c = 0; c < cols; c++) {
    for (let r = 0; r < rows; r++) {
      const first = k === 0;
      pads.push({ num: String(k + 1), name: names[k] ?? '', shape: first ? 'rect' : 'circle', pos: [r3(c * pitch), r3(rowDir * r * pitch)], size: [padSize, padSize], drill });
      k++;
    }
  }
  const w = cols * pitch, h = rows * pitch;
  const cx = ((cols - 1) * pitch) / 2, cy = (rowDir * (rows - 1) * pitch) / 2;
  return fp({
    id, name, category, refPrefix: 'J',
    description: description || `${female ? 'Connecteur femelle' : 'Barrette mâle'} ${rows}×${cols} au pas de ${pitch} mm. Origine = broche 1 (pastille carrée) ; les broches suivantes vont vers +X${rows > 1 ? `, la 2e rangée vers ${rowDir < 0 ? '−' : '+'}Y (numérotation en zigzag : 1-2 en face l'une de l'autre)` : ''}.`,
    pads,
    silk: silkBox(w, h, cx, cy),
    courtyard: [[r3(cx - w / 2 - 0.25), r3(cy - h / 2 - 0.25)], [r3(cx + w / 2 + 0.25), r3(cy + h / 2 + 0.25)]],
    body: { size: [w, h, female ? 8.5 : 2.5], offset: [r3(cx), r3(cy)], color: '#111' },
    tags: ['THT', 'barrette', female ? 'femelle' : 'mâle'],
  });
}

function terminalBlock(pitch, n, color = '#2f7fd0') {
  const drill = pitch >= 5 ? 1.3 : 1.1;
  const pad = pitch >= 5 ? 2.6 : 2.0;
  const pads = [];
  for (let k = 0; k < n; k++) pads.push({ num: String(k + 1), name: '', shape: k === 0 ? 'rect' : 'circle', pos: [r3(k * pitch), 0], size: [pad, pad], drill });
  const w = n * pitch, depth = pitch >= 5 ? 9 : 7;
  const cx = ((n - 1) * pitch) / 2;
  return fp({
    id: `bornier-${String(pitch).replace('.', '')}-${n}`,
    name: `Bornier à vis ${n} pts au pas ${pitch} mm`,
    category: 'Connecteurs', refPrefix: 'J',
    description: `Bornier à vis pour fils, ${n} points au pas de ${pitch} mm. Origine = broche 1 ; entrée des fils côté −Y.`,
    pads,
    silk: silkBox(w, depth, cx, 0.5),
    courtyard: [[r3(cx - w / 2 - 0.3), r3(0.5 - depth / 2 - 0.3)], [r3(cx + w / 2 + 0.3), r3(0.5 + depth / 2 + 0.3)]],
    body: { size: [w, depth, pitch >= 5 ? 10 : 8.5], offset: [r3(cx), 0.5], color },
    tags: ['THT', 'bornier'],
  });
}

function jstXh(n) {
  const pitch = 2.5;
  const pads = [];
  for (let k = 0; k < n; k++) pads.push({ num: String(k + 1), name: '', shape: k === 0 ? 'rect' : 'oval', pos: [r3(k * pitch), 0], size: [1.7, 2.0], drill: 0.95 });
  const w = (n - 1) * pitch + 4.9;
  const cx = ((n - 1) * pitch) / 2;
  return fp({
    id: `jst-xh-${n}`, name: `JST XH ${n} broches (B${n}B-XH-A)`, category: 'Connecteurs', refPrefix: 'J',
    description: `Embase JST XH droite, ${n} broches au pas de 2,5 mm. Origine = broche 1 ; détrompeur côté +Y.`,
    pads,
    silk: silkBox(w, 5.75, cx, -0.7),
    courtyard: [[r3(cx - w / 2 - 0.3), -3.9], [r3(cx + w / 2 + 0.3), 2.5]],
    body: { size: [w, 5.75, 7], offset: [r3(cx), -0.7], color: '#f2efe4' },
    tags: ['THT', 'JST'],
  });
}

/** Boîtier DIP : n broches, rangées espacées de span (7,62 par défaut). */
function dip(id, name, n, { span = 7.62, names = [], defaults, description, refPrefix = 'U', category = 'Circuits intégrés' } = {}) {
  const half = n / 2;
  const pads = [];
  const y0 = ((half - 1) * 2.54) / 2;
  for (let k = 0; k < half; k++) pads.push({ num: String(k + 1), name: names[k] ?? '', shape: k === 0 ? 'rect' : 'oval', pos: [r3(-span / 2), r3(y0 - k * 2.54)], size: [1.6, 1.6], drill: 0.8 });
  for (let k = 0; k < half; k++) pads.push({ num: String(half + k + 1), name: names[half + k] ?? '', shape: 'oval', pos: [r3(span / 2), r3(-y0 + k * 2.54)], size: [1.6, 1.6], drill: 0.8 });
  const h = (half - 1) * 2.54 + 2.6;
  return fp({
    id, name, category, refPrefix,
    description: description ?? `DIP-${n} traversant, rangées à ${span} mm. Broche 1 en haut à gauche.`,
    pads,
    silk: [...silkBox(span - 1.6, h), pin1Dot(-span / 2 + 1.4, y0 + 0.9)],
    courtyard: [[r3(-span / 2 - 1.1), r3(-h / 2 - 0.3)], [r3(span / 2 + 1.1), r3(h / 2 + 0.3)]],
    body: { size: [6.4, h - 0.3, 3.5], color: '#1c1c1c' },
    tags: ['THT', 'DIP'],
    defaults,
  });
}

function mountingHole(id, name, drill, padDia) {
  return fp({
    id, name, category: 'Mécanique', refPrefix: 'H',
    description: padDia
      ? `Trou de fixation Ø${drill} métallisé, pastille Ø${padDia} (peut être relié à GND).`
      : `Trou de fixation Ø${drill} non métallisé (aucun cuivre).`,
    pads: [{ num: '1', name: padDia ? 'MH' : '', shape: 'circle', pos: [0, 0], size: padDia ? [padDia, padDia] : [drill, drill], drill, plated: !!padDia }],
    silk: [{ kind: 'circle', pts: [[0, 0]], r: r3((padDia || drill) / 2 + 0.6), width: 0.15 }],
    // Encombrement serré : un carré plus large sortirait des coins arrondis de la carte.
    courtyard: [[-(padDia || drill) / 2 - 0.25, -(padDia || drill) / 2 - 0.25], [(padDia || drill) / 2 + 0.25, (padDia || drill) / 2 + 0.25]],
    tags: ['fixation'],
  });
}

// ── Modules ────────────────────────────────────────────────────

const S3_NAMES = [
  'GND', '3V3', 'EN', 'IO4', 'IO5', 'IO6', 'IO7', 'IO15', 'IO16', 'IO17', 'IO18', 'IO8', 'IO19', 'IO20',
  'IO3', 'IO46', 'IO9', 'IO10', 'IO11', 'IO12', 'IO13', 'IO14', 'IO21', 'IO47', 'IO48', 'IO45',
  'IO0', 'IO35', 'IO36', 'IO37', 'IO38', 'IO39', 'IO40', 'IO41', 'IO42', 'RXD0', 'TXD0', 'IO2', 'IO1', 'GND',
];

function esp32s3Wroom1() {
  const pads = [];
  for (let k = 0; k < 14; k++) pads.push({ num: String(k + 1), name: S3_NAMES[k], shape: 'rect', pos: [-8.75, r3(5.26 - k * 1.27)], size: [1.5, 0.9] });
  for (let k = 0; k < 12; k++) pads.push({ num: String(15 + k), name: S3_NAMES[14 + k], shape: 'rect', pos: [r3(-6.985 + k * 1.27), -12.5], size: [0.9, 1.5] });
  for (let k = 0; k < 14; k++) pads.push({ num: String(27 + k), name: S3_NAMES[26 + k], shape: 'rect', pos: [8.75, r3(-11.25 + k * 1.27)], size: [1.5, 0.9] });
  pads.push({ num: '41', name: 'GND', shape: 'rect', pos: [-1.5, -2.46], size: [3.7, 3.7], paste: true });
  return fp({
    id: 'esp32-s3-wroom-1',
    name: 'ESP32-S3-WROOM-1 (module Wi-Fi/BLE)',
    category: 'Modules',
    refPrefix: 'U',
    description: [
      'Module Espressif ESP32-S3-WROOM-1 (18 × 25,5 mm), 41 pastilles au pas de 1,27 mm. Origine = centre du module, ANTENNE vers +Y.',
      'Broches 1-14 à gauche (haut → bas), 15-26 en bas (gauche → droite), 27-40 à droite (bas → haut), 41 = pastille de masse centrale.',
      'Alimentation 3,3 V (3V3, broche 2) — prévoir ≥ 500 mA, 10 µF + 100 nF au plus près. EN (3) : RC 10 kΩ / 1 µF vers 3V3.',
      'Broches de démarrage (strapping) : IO0 (27, bas = mode téléchargement), IO3, IO45, IO46 — ne pas les forcer. USB natif : IO19 = D−, IO20 = D+.',
      'IO35-37 indisponibles sur les variantes à PSRAM octale (N8R8, N16R8). UART0 : TXD0 (37), RXD0 (36).',
      'ZONE D\'ANTENNE : aucun cuivre (toutes couches) sous l\'antenne ; idéalement l\'antenne dépasse du bord de carte ou est au ras du bord.',
      'Géométrie d\'après la fiche Espressif — à contrôler avant fabrication.',
    ].join(' '),
    pads,
    silk: [...silkBox(18, 25.5, 0, 0), { kind: 'line', pts: [[-9, 6.75], [9, 6.75]], width: 0.15 }],
    courtyard: [[-9.75, -13.5], [9.75, 12.95]],
    keepouts: [{ min: [-9, 6.75], max: [9, 12.75], layers: ['F', 'B'], label: 'antenne' }],
    body: { size: [18, 25.5, 3.1], color: '#b8bcc2' },
    tags: ['ESP32', 'Wi-Fi', 'BLE', 'module'],
    defaults: { value: 'ESP32-S3-WROOM-1-N8R8', mpn: 'ESP32-S3-WROOM-1-N8R8' },
  });
}

function tactSwitch() {
  return fp({
    id: 'bouton-6x6', name: 'Bouton poussoir 6 × 6 mm traversant', category: 'Interface', refPrefix: 'SW',
    description: 'Bouton tactile 6 × 6 mm. Les deux pastilles « 1 » sont reliées entre elles dans le bouton, de même que les deux « 2 ».',
    pads: [
      { num: '1', name: '', shape: 'circle', pos: [-3.25, 2.25], size: [2, 2], drill: 1.0 },
      { num: '2', name: '', shape: 'circle', pos: [-3.25, -2.25], size: [2, 2], drill: 1.0 },
      { num: '1', name: '', shape: 'circle', pos: [3.25, 2.25], size: [2, 2], drill: 1.0 },
      { num: '2', name: '', shape: 'circle', pos: [3.25, -2.25], size: [2, 2], drill: 1.0 },
    ],
    silk: silkBox(6, 6),
    courtyard: [[-4.5, -3.5], [4.5, 3.5]],
    body: { size: [6, 6, 5], color: '#333' },
    tags: ['THT', 'bouton'],
  });
}

// ── Catalogue ──────────────────────────────────────────────────

const R_DEF = (v, lcsc = '') => ({ value: v, lcsc });

export const BUILTIN_FOOTPRINTS = [
  chip('R', '0402'), chip('R', '0603'), chip('R', '0805', { defaults: R_DEF('10k', 'C17414') }), chip('R', '1206'),
  chip('C', '0402'), chip('C', '0603'), chip('C', '0805', { defaults: R_DEF('100nF', 'C49678') }), chip('C', '1206'),
  chip('L', '0805'), chip('F', '1206'),
  chip('D', '0603', { names: ['K', 'A'] }), chip('D', '0805', { names: ['K', 'A'] }),
  twoPinDiode('sod-123', 'Diode SOD-123', { pad: [0.91, 1.22], at: 1.635, body: [2.7, 1.6, 1.1], defaults: { value: '1N4148W', mpn: '1N4148W', lcsc: '' } }),
  twoPinDiode('sma', 'Diode SMA (DO-214AC)', { pad: [2.5, 1.7], at: 2.0, body: [4.3, 2.6, 2.1], defaults: { value: 'SS34', mpn: 'SS34', lcsc: '' } }),
  twoPinDiode('smb', 'Diode SMB (DO-214AA) — TVS', { pad: [2.5, 2.3], at: 2.15, body: [4.3, 3.6, 2.2], description: 'TVS unidirectionnelle : 1 = cathode (K), 2 = anode (A).', defaults: { value: 'SMBJ24A', mpn: 'SMBJ24A', lcsc: '' } }),
  sot23('sot-23', 'SOT-23 (3 broches)'),
  sot23('ao3400', 'MOSFET N AO3400A (SOT-23)', ['G', 'S', 'D'], { description: 'MOSFET canal N 30 V / 5,7 A, commande logique 3,3 V. 1 = G (grille), 2 = S (source), 3 = D (drain).', defaults: { value: 'AO3400A', mpn: 'AO3400A', lcsc: 'C20917' } }),
  sot23_5('sot-23-5', 'SOT-23-5'),
  sot223('sot-223', 'SOT-223'),
  sot223('ams1117-3v3', 'Régulateur AMS1117-3.3 (SOT-223)', ['GND', 'VOUT', 'VIN', 'VOUT'], {
    description: 'Régulateur linéaire 3,3 V / 1 A. 1 = GND, 2 = VOUT, 3 = VIN, languette = VOUT. Condensateurs : 10 µF en entrée ET en sortie (stabilité). Dissipation (VIN−3,3)×I : 5 V → 3,3 V sous 500 mA ≈ 0,85 W, prévoir du cuivre sur la languette.',
    defaults: { value: 'AMS1117-3.3', mpn: 'AMS1117-3.3', lcsc: 'C6186' },
  }),
  soic('soic-8', 'SOIC-8 (3,9 mm)', 8),
  soic('soic-14', 'SOIC-14 (3,9 mm)', 14, { body: [3.9, 8.7, 1.5] }),
  soic('soic-16', 'SOIC-16 (3,9 mm)', 16, { body: [3.9, 9.9, 1.5] }),
  soic('sp3485', 'Émetteur-récepteur RS485 SP3485 / MAX3485 (SOIC-8)', 8, {
    names: ['RO', 'RE', 'DE', 'DI', 'GND', 'A', 'B', 'VCC'],
    category: 'Interface',
    description: 'Émetteur-récepteur RS485/RS422 3,3 V, semi-duplex. 1 = RO (réception → MCU RX), 2 = /RE (réception active à 0), 3 = DE (émission active à 1), 4 = DI (MCU TX), 5 = GND, 6 = A, 7 = B, 8 = VCC (3,3 V). Relier /RE et DE ensemble à une broche de direction. 100 nF sur VCC. Côté bus : résistance de terminaison 120 Ω entre A et B (avec cavalier), polarisation 680 Ω (A→VCC, B→GND), protection TVS (SM712 ou 2× SMBJ) recommandée.',
    defaults: { value: 'SP3485EN', mpn: 'SP3485EN-L/TR', lcsc: 'C8963' },
  }),
  dip('pc817', 'Optocoupleur PC817 (DIP-4)', 4, {
    names: ['A', 'K', 'E', 'C'],
    category: 'Interface',
    description: 'Optocoupleur à phototransistor. 1 = anode LED, 2 = cathode LED, 3 = émetteur, 4 = collecteur. Entrée 24 V : ~4,7 kΩ en série (≈ 5 mA). Isolement : laisser ≥ 2 mm sans cuivre entre côtés primaire et secondaire.',
    refPrefix: 'U',
    defaults: { value: 'PC817', mpn: 'PC817C', lcsc: '' },
  }),
  dip('dip-8', 'DIP-8', 8),
  esp32s3Wroom1(),
  tactSwitch(),
  terminalBlock(5.08, 2), terminalBlock(5.08, 3), terminalBlock(5.08, 4),
  terminalBlock(3.5, 2, '#2e9e4f'), terminalBlock(3.5, 3, '#2e9e4f'), terminalBlock(3.5, 4, '#2e9e4f'),
  jstXh(2), jstXh(3), jstXh(4), jstXh(5), jstXh(6),
  pinHeader('pinheader-1x02', 'Barrette 1×2 (cavalier)', 2), pinHeader('pinheader-1x03', 'Barrette 1×3', 3),
  pinHeader('pinheader-1x04', 'Barrette 1×4', 4), pinHeader('pinheader-1x06', 'Barrette 1×6', 6),
  pinHeader('pinheader-1x08', 'Barrette 1×8', 8), pinHeader('pinheader-2x05', 'Barrette 2×5', 5, 2),
  pinHeader('pinheader-2x20', 'Barrette 2×20', 20, 2),
  mountingHole('trou-m3', 'Trou de fixation M3 métallisé', 3.2, 6.0),
  mountingHole('trou-m3-npth', 'Trou de fixation M3 non métallisé', 3.2, 0),
  mountingHole('trou-m2_5', 'Trou de fixation M2.5 métallisé', 2.7, 5.0),
];

const BY_ID = new Map(BUILTIN_FOOTPRINTS.map((f) => [f.id, f]));

/** Empreintes générées à la demande : pinheader-RxC / pinsocket-RxC (R rangées, C colonnes). */
export function dynamicFootprint(id) {
  const m = /^pin(header|socket)-([12])x(\d{2})$/.exec(id);
  if (!m) return null;
  const rows = Number(m[2]), cols = Number(m[3]);
  if (cols < 1 || cols > 40) return null;
  const female = m[1] === 'socket';
  return pinHeader(id, `${female ? 'Connecteur femelle' : 'Barrette'} ${rows}×${cols}`, cols, rows, { female });
}

export function builtinFootprint(id) {
  return BY_ID.get(id) ?? dynamicFootprint(id);
}
